import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadialBarChart, RadialBar, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import { Target, Globe, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Utility function to format numbers in K, M, etc.
const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

interface Keyword {
  id: number;
  keyword_text: string;
  volume: number;
  competition: string;
  da_score?: string;
  low_cpc?: number;
  high_cpc?: number;
}

interface KDDonutChartProps {
  keywords: Keyword[];
  isLoading?: boolean;
  selectedKeywords?: Keyword[];
}

const KDDonutChart: React.FC<KDDonutChartProps> = ({ keywords, isLoading = false, selectedKeywords = [] }) => {
  // Determine which keywords to display
  const displayKeywords = selectedKeywords.length > 0 ? selectedKeywords : (keywords.length > 0 ? [keywords[0]] : []);
  
  // Calculate average KD (avoid null values and convert string to number)
  const validKeywords = displayKeywords.filter(keyword => {
    const daScore = keyword.da_score;
    if (daScore === null || daScore === undefined || daScore === '') return false;
    const numScore = Number(daScore);
    return !isNaN(numScore);
  });
  
  console.log('KDDonutChart - displayKeywords:', displayKeywords);
  console.log('KDDonutChart - validKeywords:', validKeywords);
  console.log('KDDonutChart - validKeywords da_scores:', validKeywords.map(k => k.da_score));
  
  const displayKD = validKeywords.length > 0 
    ? Math.round(validKeywords.reduce((sum, keyword) => sum + Number(keyword.da_score), 0) / validKeywords.length)
    : 0;
    
  console.log('KDDonutChart - displayKD:', displayKD);
  
  // Calculate metrics
  const totalVolume = displayKeywords.reduce((sum, keyword) => sum + (keyword.volume || 0), 0);
  const avgLowCpc = displayKeywords.length > 0 
    ? displayKeywords.reduce((sum, keyword) => sum + (Number(keyword.low_cpc) || 0), 0) / displayKeywords.length
    : 0;
  
  // Get intent with highest number (assuming competition is the intent field)
  const intentCounts: { [key: string]: number } = {};
  displayKeywords.forEach(keyword => {
    const intent = keyword.competition || '-';
    intentCounts[intent] = (intentCounts[intent] || 0) + 1;
  });
  const dominantIntent = Object.entries(intentCounts).length > 0 
    ? Object.entries(intentCounts).reduce((a, b) => intentCounts[a[0]] > intentCounts[b[0]] ? a : b)[0]
    : '-';


  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Average Difficulty</span>
          </CardTitle>
          <CardDescription>
            Average KD score for selected keywords
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          <div className="h-48 flex items-center justify-center">
            <div className="text-muted-foreground">Loading...</div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (displayKeywords.length === 0 || displayKD === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Target className="h-5 w-5" />
            <span>Average Difficulty</span>
          </CardTitle>
          <CardDescription>
            Average KD score for selected keywords
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4">
          <div className="h-48 flex items-center justify-center">
            <div className="text-center">
              <div className="text-muted-foreground mb-2">No data available</div>
              <div className="text-sm text-muted-foreground">
                Keywords need difficulty data to display
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <Target className="h-5 w-5" />
          <span>Average Difficulty</span>
        </CardTitle>
        <CardDescription>
          Average KD score for selected keywords
        </CardDescription>
      </CardHeader>
      <CardContent className="p-4">
        <div className="h-48 w-full relative">
         
    <div className="w-full max-w-sm mx-auto rounded-2xl shadow-xl">
      <div className="relative h-48 p-0">
        <ResponsiveContainer width="100%" height="100%">
          <RadialBarChart
            data={[{ name: '', value: displayKD }]}
            innerRadius="72%"
            outerRadius="92%"
            startAngle={90}
            endAngle={-270}
          >
            {/* Hide ticks & axis; just use domain for 0–100 */}
            <PolarAngleAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />

            {/* Progress track (background) */}
            <RadialBar
              dataKey="value"
              cornerRadius={24}
              background // draws a light gray track
              fill="hsl(var(--primary))" // Tailwind emerald-500
              // clockWise
            />
          </RadialBarChart>
        </ResponsiveContainer>
      </div>
    </div>

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="text-center">
              <div className="text-3xl font-bold text-foreground">{displayKD}</div>
            </div>
          </div>
        </div>
        

                  {displayKeywords.length > 0 && (
           <div className="mb-4 text-center">
            <div className="mb-3">
              <div className="text-xs text-slate-400 mb-1">
                {selectedKeywords.length > 0 ? 'Selected keywords' : 'First keyword'}
              </div>
              <div className="text-sm font-medium">
                {displayKeywords.map(k => k.keyword_text).join(', ')}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-2 text-center">
                <div className="text-xs text-slate-400">Volume</div>
                <div className="text-sm font-semibold">{formatNumber(totalVolume)}</div>
              </div>
              <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-2 text-center">
                <div className="text-xs text-slate-400">CPC</div>
                <div className="text-sm font-semibold">
                  ${avgLowCpc.toFixed(2)}
                </div>
              </div>
              <div className="rounded-xl bg-white/5 ring-1 ring-white/10 p-2 text-center">
                <div className="text-xs text-slate-400">Intent</div>
                <div className="text-sm font-semibold">
                  {dominantIntent}
                </div>
              </div>
            </div>
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white text-slate-900 hover:shadow"
                onClick={() => {
                  const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(displayKeywords[0].keyword_text)}`;
                  window.open(searchUrl, '_blank');
                }}
              >
                <Globe className="h-3 w-3" />
                SERP
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium transition focus:outline-none focus:ring-2 focus:ring-indigo-400 ring-1 ring-white/15 text-slate-200 hover:bg-white/5"
              >
                <Layers className="h-3 w-3" />
                Analyze
              </Button>
            </div>
          </div>
          )}

      </CardContent>
    </Card>
  );
};

export default KDDonutChart;
